import React, { useContext, useState } from 'react';
import {
    DetailDossierActionMenu,
    DetailDossierTitle,
    DetailDossierToolbar,
    DossierActionContext,
    useDossierActionMenuItemsFront,
} from '@europrocurement/l2d-modules';
import {
    selectedOffer,
    selectedPrescripteursSelector,
    selectedSousClientSelector,
    TiersAppDispatch,
    useOffreService,
    useTiersService,
    getDossierLibelle,
} from '@europrocurement/l2d-domain';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentDossierCtx } from '@europrocurement/l2d-modules/modules/Dossiers/providers/CurrentDossierProvider';
import { useFullLayout } from '@europrocurement/flexy-components';

const DetailDossierHeader: React.FunctionComponent = function () {
    const { dossier, siblings } = useCurrentDossierCtx();
    const { triggerAction } = useContext(DossierActionContext);
    const { selectSousClients, selectPrescripteur } = useTiersService();
    const { selectOffer } = useOffreService();
    const { dossiersUpdated, setDossiersUpdated } = React.useContext(DossierActionContext);
    const { setPageTitle, clearPageTitle } = useFullLayout();
    const dispatch = useDispatch<TiersAppDispatch>();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    React.useEffect(() => {
        if (!dossier) return;
        if (dossiersUpdated.length) {
            setDossiersUpdated([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossiersUpdated, dossier]);

    const prescripteurIdFromCurrentDossier = dossier?.prescripteurId;
    const offreIdFromCurrentDossier = dossier?.offreId;
    const clientOpereIdFromCurrentDossier = dossier?.clientOpereId;

    const prescripteur = useSelector(selectedPrescripteursSelector);
    const offre = useSelector(selectedOffer);
    const clientOpere = useSelector(selectedSousClientSelector);

    // Update browser tab
    React.useEffect(() => {
        if (dossier) {
            setPageTitle(`Dossier ${dossier?.numero} | ${getDossierLibelle(dossier)}`);
        }
        return () => {
            clearPageTitle();
        };
    }, [clearPageTitle, clientOpere?.raisonSociale, dossier, setPageTitle]);

    React.useEffect(() => {
        if (!prescripteurIdFromCurrentDossier) return;

        dispatch(selectPrescripteur({ id: prescripteurIdFromCurrentDossier?.toString() }));
    }, [dispatch, prescripteurIdFromCurrentDossier, selectPrescripteur]);

    React.useEffect(() => {
        if (!offreIdFromCurrentDossier) return;

        dispatch(selectOffer({ id: offreIdFromCurrentDossier }));
    }, [dispatch, offreIdFromCurrentDossier, selectOffer]);

    React.useEffect(() => {
        if (!clientOpereIdFromCurrentDossier) return;

        dispatch(selectSousClients({ id: clientOpereIdFromCurrentDossier }));
    }, [dispatch, clientOpereIdFromCurrentDossier, selectSousClients]);

    const refreshClientOpere = () => {
        if (!clientOpereIdFromCurrentDossier) return;

        dispatch(selectSousClients({ id: clientOpereIdFromCurrentDossier }));
    };

    // URLs et données formatées
    const clientOpereSocieteDotComUrl = React.useMemo(() => {
        if (!clientOpere?.siren) return undefined;
        return `https://www.societe.com/cgi-bin/fiche?rncs=${clientOpere.siren}`;
    }, [clientOpere?.siren]);

    /** Données du dossier formatées pour l'affichage */
    const formattedDossier = React.useMemo(
        () => ({
            statut: dossier?.statutLibelle as string,
            statutId: dossier?.statutId as number,
            societeRef: dossier?.societeRef as string,
            id: dossier?.id as number,
            collectionId: dossier?.collectionId as number,
            formalityIds: (dossier?.formalites || []).map((f) => f.id),
        }),
        [
            dossier?.statutLibelle,
            dossier?.societeRef,
            dossier?.statutId,
            dossier?.id,
            dossier?.collectionId,
            dossier?.formalites,
        ],
    );

    const formattedPrescripteur: {
        id: number;
        raisonSociale: string;
        codeComptable: string;
        urlToDetails?: string;
        demo?: boolean;
    } = {
        id: prescripteur?.id as number,
        raisonSociale: (prescripteur?.nomDentreprise as string) || 'Pas de raison sociale',
        codeComptable: prescripteur?.relationsSocietes[0].codeClient || 'Pas de code comptable',
        urlToDetails: undefined,
        demo: prescripteur?.demo || false,
    };

    const formattedOffre: { libelle: string } = {
        libelle: offre?.libelle || 'Aucune offre',
    };

    const formattedClientOpere = React.useMemo(
        () => ({
            raisonSociale: clientOpere?.raisonSociale || getDossierLibelle(dossier),
            siren: clientOpere?.siren,
            urlToSocieteDotCom: clientOpereSocieteDotComUrl,
            id: dossier?.clientOpereId as number,
        }),
        [clientOpere?.raisonSociale, clientOpere?.siren, clientOpereSocieteDotComUrl, dossier],
    );

    const isMultiDossiers = siblings && siblings.length > 1;

    const { dossierDetatilQuickActionFront } = useDossierActionMenuItemsFront({
        dossier,
        triggerAction,
        setAnchorEl,
    });

    if (!dossier) {
        console.log('Dossier non trouvé');
        return null;
    }

    return (
        <>
            <DetailDossierToolbar
                dossier={dossier}
                siblingDossiers={siblings}
                isMultiDossiers={isMultiDossiers}
            >
                <DetailDossierActionMenu
                    actions={dossierDetatilQuickActionFront}
                    anchorEl={anchorEl}
                    setAnchorEl={setAnchorEl}
                />
            </DetailDossierToolbar>
            <DetailDossierTitle
                dossier={formattedDossier}
                prescripteur={formattedPrescripteur}
                offre={formattedOffre}
                clientOpere={formattedClientOpere}
                color="primary"
                icon={isMultiDossiers ? 'folders' : 'folder'}
                onEditSousClientSuccess={refreshClientOpere}
                source="FRONT"
            />
        </>
    );
};

export default DetailDossierHeader;
